import { useEffect, useRef } from 'react';

/**
 * Fires `onIdle` after `timeoutMs` of no user interaction.
 *
 * "Interaction" = pointer / keyboard / scroll / touch on the window.
 * Each interaction restarts the timer (throttled to once per second so
 * a stream of mousemove events doesn't thrash). The hook is inert while
 * `enabled` is false — used to suspend the timer when no one is signed
 * in, and (importantly) while the logout-warning dialog is already up,
 * so wiggling the mouse can't silently cancel the countdown; the user
 * must click "Stay signed in".
 *
 * This is the mechanism behind the 15-minute inactivity logout: the
 * AuthProvider points `onIdle` at "show the 60s warning", and the
 * dialog's own countdown does the final logout.
 */
const ACTIVITY_EVENTS = [
  'mousedown',
  'mousemove',
  'keydown',
  'scroll',
  'touchstart',
  'wheel',
] as const;

const RESET_THROTTLE_MS = 1_000;

export function useIdleTimeout({
  timeoutMs,
  onIdle,
  enabled,
}: {
  timeoutMs: number;
  onIdle: () => void;
  enabled: boolean;
}): void {
  // Keep the latest callback in a ref so changing its identity each
  // render doesn't re-subscribe the listeners / restart the timer.
  const onIdleRef = useRef(onIdle);
  useEffect(() => {
    onIdleRef.current = onIdle;
  }, [onIdle]);

  useEffect(() => {
    if (!enabled) return;

    let timer: number | undefined;
    let lastReset = 0;

    const fire = () => onIdleRef.current();

    const arm = () => {
      window.clearTimeout(timer);
      timer = window.setTimeout(fire, timeoutMs);
    };

    const onActivity = () => {
      const now = Date.now();
      if (now - lastReset < RESET_THROTTLE_MS) return;
      lastReset = now;
      arm();
    };

    arm(); // start counting from mount
    for (const ev of ACTIVITY_EVENTS) {
      window.addEventListener(ev, onActivity, { passive: true });
    }

    return () => {
      window.clearTimeout(timer);
      for (const ev of ACTIVITY_EVENTS) {
        window.removeEventListener(ev, onActivity);
      }
    };
  }, [enabled, timeoutMs]);
}
